import { useAuth0 } from '@auth0/auth0-react';
import {
  useAddressesByIds,
  useOrganisationRoutes,
  useParties,
  useRoadLegs,
  useSitesWithMembers,
} from '@my/directory-api/hooks';
import { useFerryBookings } from '@my/ferry-api/hooks';
import {
  useBookingWorklist,
  useCargoDefinitions,
  useServiceTags,
  type BookingWorklistItem,
  type CargoPropertyGroupsPayload,
} from '@my/tms-api';
import { cn } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { useShippingIntegration } from '../ferry/shippingIntegration';
import { useCrossingCatalogue } from '../ferry/useCrossingCatalogue';
import { resolveOrganisationRoutes } from '../orders/lib/route';
import { HorizonField } from '../views/HorizonField';
import { BookingIntelligence } from './components/BookingIntelligence';
import { BookingQueue } from './components/BookingQueue';
import { buildBookingRows, roadLegsToRoute, type AddressLite } from './lib/buildBookingRows';
import { buildIntelligence } from './lib/buildIntelligence';
import { HORIZON_OFFSET, type BookingRow } from './lib/mockData';

/** Stable empty list so the worklist memos don't rerun on every render. */
const EMPTY_ITEMS: BookingWorklistItem[] = [];

/** Free-text match across a row's identity, route, corridor and reference. */
function rowMatchesSearch(r: BookingRow, q: string): boolean {
  if (!q) return true;
  return [
    r.orderNumber,
    r.customer,
    r.cargoLabel,
    r.destination,
    r.corridor,
    r.best?.routeShort,
    r.best?.arrivalPort,
    r.best?.operator,
    r.bookingRef,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q);
}

/**
 * Bookings (Ferry) — presentation prototype.
 *
 * The port-departure-centric booking cockpit (myTMS Ferry Booking spec): a
 * control bar (Depot · Horizon · Port), two queues (To Book / Booked) and the
 * "my i" Booking Intelligence overlay — same shell + colours as the Plan
 * screen. The queues are the TMS booking worklist with the carrier's status
 * from My.Ferry; the panels book through the same flow as the order's task
 * screen. The my-i panel's capacity swaps and forecast are still sample data.
 */

export default function BookingsPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  // Real depot list — organisation Sites, name-sorted, auto-pick the first.
  const sitesQuery = useSitesWithMembers(orgId);
  const sites = useMemo(
    () => (sitesQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sitesQuery.data],
  );
  const [depotId, setDepotId] = useState<string | null>(null);
  useEffect(() => {
    const first = sites[0];
    if (!first) return;
    if (!depotId || !sites.some((s) => s.id === depotId)) setDepotId(first.id);
  }, [sites, depotId]);
  const depotLabel = sites.find((s) => s.id === depotId)?.name ?? 'Depot';

  // Horizon — Today plus 0–5 additional days (a cumulative range from today).
  const [extraDays, setExtraDays] = useState(0);

  // Port filter — All / each departure port.
  const [portId, setPortId] = useState<string>('all');

  // Free-text search across both queues.
  const [search, setSearch] = useState('');

  const [assistantOpen, setAssistantOpen] = useState(false);

  // Reference "now" for the recommendation's cutoff countdown — re-stamped on
  // Rerun so the cutoffs (and the derived counts) refresh against the clock.
  const [now, setNow] = useState(() => new Date());

  // ── Real data ─────────────────────────────────────────────────────────
  // Worklist (orders + booking state) → hydrate the origin/destination
  // addresses (for the crossing test + destination town), party names and
  // cargo labels from Directory/TMS, then build the ferry rows. The sailing
  // recommendation + the my-i panel below stay on mock (parked engine).
  const worklistQuery = useBookingWorklist(orgId ?? null);
  const items = worklistQuery.data ?? EMPTY_ITEMS;

  // The carrier's side of every booked row in one call: the Ferry bookings this
  // app made, joined to the rows by externalReference (= the TMS booking id).
  // The window follows the rows' own sail dates, a day either side, so a
  // crossing that sailed before the delivery horizon still shows its status.
  const ferryWindow = useMemo(() => {
    const day = (base: Date, offset: number) => {
      const d = new Date(base);
      d.setUTCHours(0, 0, 0, 0);
      d.setUTCDate(d.getUTCDate() + offset);
      return d.toISOString();
    };
    const sailDates = items.flatMap((it) => (it.booking?.sailDate ? [it.booking.sailDate] : []));
    if (sailDates.length === 0) return { from: day(now, -1), to: day(now, 16) };
    const first = sailDates.reduce((a, b) => (a < b ? a : b));
    const last = sailDates.reduce((a, b) => (a > b ? a : b));
    return { from: day(new Date(`${first}T00:00:00Z`), -1), to: day(new Date(`${last}T00:00:00Z`), 2) };
  }, [items, now]);
  // Asked only with the integration on, or when a row was already booked at
  // the carrier, so such a booking can still be cancelled there.
  const { enabled: integrationEnabled } = useShippingIntegration(orgId ?? null);
  const holdsFerryBookings = useMemo(
    () => items.some((it) => !!it.booking?.ferryBookingId),
    [items],
  );
  const ferryQuery = useFerryBookings(
    integrationEnabled || holdsFerryBookings ? (orgId ?? null) : null,
    { sourceApp: 'Tms', from: ferryWindow.from, to: ferryWindow.to },
  );

  const addressIds = useMemo(() => {
    const ids = new Set<string>();
    for (const it of items) {
      if (it.originAddressId) ids.add(it.originAddressId);
      if (it.destinationAddressId) ids.add(it.destinationAddressId);
      if (it.departurePortAddressId) ids.add(it.departurePortAddressId);
      // Both port tasks of a booking, so the booked route can name its ports.
      for (const t of it.booking?.tasks ?? []) ids.add(t.addressId);
    }
    return [...ids];
  }, [items]);

  const addressesQuery = useAddressesByIds(orgId ?? null, addressIds);
  const partiesQuery = useParties(orgId ?? null, null);
  const cargoQuery = useCargoDefinitions(orgId ?? null);
  const serviceTagsQuery = useServiceTags(orgId ?? null);
  // The organisation's route picks under either key style, as the order form books them.
  const orgRoutesQuery = useOrganisationRoutes(orgId ?? null);
  const crossingCatalogue = useCrossingCatalogue();

  // Built in two passes, the same shape the order journey panel uses.
  //
  // Pass one ranks the picks on straight-line distance — cheap, and enough to
  // decide WHICH port is worth asking a metered routing API about. Pass two
  // replaces the recommended leg with a real truck driving time. Ranking every
  // pick on a routed leg would be a call per pick per row for numbers nobody
  // reads; this is one per row, deduplicated, and cached for twelve hours.
  const rowContext = useMemo(() => {
    const addresses = new Map<string, AddressLite>((addressesQuery.data ?? []).map((a) => [a.id, a]));
    // What the carrier's booking needs from the cargo: its temperature setting
    // (the plug-in: the definition's lower limit for now, not the order's own
    // setpoint), its equipment keys (the vehicle type), and whether it is a
    // container (the booking type).
    const cargo = new Map(
      (cargoQuery.data ?? []).map((c) => {
        let temperatureC: number | null = null;
        try {
          const groups = c.propertyGroupsJson
            ? (JSON.parse(c.propertyGroupsJson) as CargoPropertyGroupsPayload)
            : null;
          temperatureC = groups?.temperature?.minTempC ?? groups?.temperature?.maxTempC ?? null;
        } catch {
          // Unreadable property groups: not temperature-controlled.
        }
        return [
          c.id,
          { temperatureC, equipmentKeys: c.equipmentCatalogueKeys, container: c.cargoType === 'Container' },
        ];
      }),
    );
    // A hazardous load is one tagged ADR / hazardous, as the order page routes it.
    const hazardousTagIds = new Set(
      (serviceTagsQuery.data ?? [])
        .filter((tag) =>
          /\b(adr|haz|hazmat|hazardous|dangerous)\b/i.test(`${tag.name} ${tag.description ?? ''}`),
        )
        .map((tag) => tag.id),
    );
    return {
      addresses,
      partyNames: new Map((partiesQuery.data ?? []).map((p) => [p.id, p.name])),
      cargoLabels: new Map((cargoQuery.data ?? []).map((c) => [c.id, c.displayName])),
      today: now,
      ferryByBookingId: new Map((ferryQuery.data ?? []).map((b) => [b.externalReference, b])),
      picks: resolveOrganisationRoutes(orgRoutesQuery.data ?? [], crossingCatalogue.routeByKey),
      carriersByKey: crossingCatalogue.carrierByKey,
      hazardousTagIds,
      cargo,
    };
  }, [
    addressesQuery.data,
    partiesQuery.data,
    cargoQuery.data,
    serviceTagsQuery.data,
    ferryQuery.data,
    orgRoutesQuery.data,
    crossingCatalogue.routeByKey,
    crossingCatalogue.carrierByKey,
    now,
  ]);

  const estimated = useMemo(() => buildBookingRows(items, rowContext), [items, rowContext]);

  const destinationAddressIdOf = useMemo(() => {
    const byOrder = new Map(items.map((it) => [it.orderId, it.destinationAddressId]));
    return (orderId: string) => byOrder.get(orderId);
  }, [items]);

  // Only the unbooked rows: a booked row's leg is a fact of the booking, and
  // the queue shows its carrier reference rather than a recommendation.
  const legsToRoute = useMemo(
    () => roadLegsToRoute(estimated.toBook, rowContext.addresses, destinationAddressIdOf),
    [estimated.toBook, rowContext.addresses, destinationAddressIdOf],
  );

  const roadLegsQuery = useRoadLegs(
    orgId ?? null,
    useMemo(
      () =>
        legsToRoute.map((leg) => ({
          fromLatitude: leg.from.latitude,
          fromLongitude: leg.from.longitude,
          toLatitude: leg.to.latitude,
          toLongitude: leg.to.longitude,
          toPlaceId: leg.to.placeId,
          toAddressId: leg.to.addressId,
        })),
      [legsToRoute],
    ),
  );

  const { toBook: allToBook, booked: allBooked } = useMemo(() => {
    const routed = roadLegsQuery.data;
    if (!routed?.some((leg) => leg.minutes != null)) return estimated;

    // Keyed by port and rounded destination, not by order: several orders to
    // the same customer through the same port are one leg, which is why
    // `roadLegsToRoute` deduplicates them in the first place.
    const byKey = new Map<string, { minutes: number; km: number }>();
    for (const answer of routed) {
      const leg = legsToRoute[answer.index];
      if (!leg || answer.minutes == null) continue;
      byKey.set(
        `${leg.portName}>${leg.to.latitude.toFixed(3)},${leg.to.longitude.toFixed(3)}`,
        { minutes: answer.minutes, km: answer.kilometres ?? 0 },
      );
    }

    return buildBookingRows(items, {
      ...rowContext,
      resolveRoad: (orderId, portName) => {
        const addressId = destinationAddressIdOf(orderId);
        const dest = addressId ? rowContext.addresses.get(addressId) : undefined;
        if (dest?.latitude == null || dest.longitude == null) return null;
        return (
          byKey.get(
            `${portName}>${dest.latitude.toFixed(3)},${dest.longitude.toFixed(3)}`,
          ) ?? null
        );
      },
    });
  }, [estimated, items, rowContext, roadLegsQuery.data, legsToRoute, destinationAddressIdOf]);

  // Port chips reflect the departure ports actually in play, not a fixed list.
  const availablePorts = useMemo(
    () =>
      Array.from(new Set([...allToBook, ...allBooked].map((r) => r.portId)))
        .filter((p) => p && p !== 'unassigned')
        .sort((a, b) => a.localeCompare(b)),
    [allToBook, allBooked],
  );

  // If the selected port drops out of the available set, fall back to All.
  useEffect(() => {
    if (portId !== 'all' && !availablePorts.includes(portId)) setPortId('all');
  }, [availablePorts, portId]);

  const toBook = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allToBook.filter(
      (r) =>
        HORIZON_OFFSET[r.horizon] <= extraDays &&
        (portId === 'all' || r.portId === portId) &&
        rowMatchesSearch(r, q),
    );
  }, [allToBook, extraDays, portId, search]);
  const booked = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allBooked.filter(
      (r) =>
        HORIZON_OFFSET[r.horizon] <= extraDays &&
        (portId === 'all' || r.portId === portId) &&
        rowMatchesSearch(r, q),
    );
  }, [allBooked, extraDays, portId, search]);

  // myI panel content — exceptions (no route / cutoff passed) + proposals
  // (catchable recommendation) derived from the in-view To-Book rows. Swaps +
  // the capacity forecast in the panel are still parked mock (need capacity data).
  const intel = useMemo(() => buildIntelligence(toBook), [toBook]);

  const horizonLabel = extraDays === 0 ? 'Today' : `Today +${extraDays}`;

  // Rerun the recommendation: re-pull the worklist and re-stamp "now" so the
  // suggested routes, cutoffs and queue counts all recompute against the clock.
  const rerun = () => {
    setNow(new Date());
    void worklistQuery.refetch();
  };
  const rerunning = worklistQuery.isFetching;

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      {/* Control bar — title + Depot + Horizon + Port, MyI on the right. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-t-lg border border-slate-200 bg-white px-3 py-2">
        <h1 className="text-sm font-semibold">
          <span className="text-slate-700">Booking</span>
        </h1>

        <Field label="Depot">
          <select
            value={depotId ?? ''}
            onChange={(e) => setDepotId(e.target.value || null)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {sites.length === 0 && <option value="">No sites</option>}
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        <HorizonField value={extraDays} onChange={setExtraDays} />

        <Field label="Port">
          <div className="flex flex-wrap items-center gap-1.5">
            <PortChip active={portId === 'all'} onClick={() => setPortId('all')}>
              All
            </PortChip>
            {availablePorts.map((port) => (
              <PortChip key={port} active={portId === port} onClick={() => setPortId(port)}>
                {port}
              </PortChip>
            ))}
          </div>
        </Field>

        <span className="ml-auto inline-flex items-center gap-3 text-xs text-slate-500">
          <LiveClock />

          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bookings…"
              className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <button
            type="button"
            onClick={() => setAssistantOpen(true)}
            aria-label="Open booking intelligence"
            className={cn(
              'relative shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-100',
              assistantOpen && 'opacity-40',
            )}
          >
            <MyIIcon className="h-7 w-7 animate-pulse" />
          </button>
        </span>
      </div>

      {/* Body — the two queues. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto pb-2 pt-3">
        <BookingQueue
          title="To book"
          count={toBook.length}
          rows={toBook}
          emptyHint="Nothing to book in this horizon / port."
          expandable
          orgId={orgId ?? null}
        />
        <BookingQueue
          title="Booked"
          count={booked.length}
          rows={booked}
          emptyHint="No bookings in this horizon / port yet."
          orgId={orgId ?? null}
        />
      </div>

      <BookingIntelligence
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        depotLabel={depotLabel}
        horizonLabel={horizonLabel}
        pipelineCount={toBook.length}
        exceptions={intel.exceptions}
        proposals={intel.proposals}
        onRerun={rerun}
        rerunning={rerunning}
      />
    </div>
  );
}

/** Live wall clock — real current date + time, ticking every second. */
function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const date = now.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
  const time = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full bg-emerald-500" />
      <span className="tabular-nums">
        {date} · {time}
      </span>
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      {children}
    </div>
  );
}

function PortChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
        active
          ? 'border-slate-700 bg-slate-700 text-white'
          : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}
